import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, lazy, Suspense, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { Toaster } from "@/components/ui/sonner";
import { LocaleProvider, useLocale } from "@/i18n/LocaleProvider";
import { LOCALE_HTML_LANG, stripLocale, getLocaleFromPathname } from "@/i18n/types";
import { OpenAiPixel } from "@/components/analytics/OpenAiPixel";
import { MetaPixel, META_PIXEL_ID } from "@/components/analytics/MetaPixel";

// Componentes não-críticos: tirados do bundle inicial para reduzir JS no caminho crítico.
// Carregam após `requestIdleCallback`, fora do LCP/FCP. Isso evita pagar pelo custo de
// react-hook-form + zod + framer-motion (FloatingWhatsApp/FinalCtaForm) no primeiro paint.
const FloatingWhatsApp = lazy(() =>
  import("@/components/layout/FloatingWhatsApp").then((m) => ({ default: m.FloatingWhatsApp })),
);
const AtenderbemWebchat = lazy(() =>
  import("@/components/layout/AtenderbemWebchat").then((m) => ({ default: m.AtenderbemWebchat })),
);
const MobileStickyCTA = lazy(() =>
  import("@/components/layout/MobileStickyCTA").then((m) => ({ default: m.MobileStickyCTA })),
);
const FinalCtaForm = lazy(() =>
  import("@/components/sections/FinalCtaForm").then((m) => ({ default: m.FinalCtaForm })),
);
const CookieConsentBanner = lazy(() =>
  import("@/components/layout/CookieConsentBanner").then((m) => ({ default: m.CookieConsentBanner })),
);

/**
 * Rotas (sem prefixo de locale) que já têm seu próprio formulário.
 */
const ROUTES_WITH_OWN_FORM = new Set<string>([
  "/",
  "/contato",
  "/ouvidoria",
  "/ia",
  "/equipe",
  "/cases",
]);

/**
 * LPs isoladas de tráfego pago: renderizam header/footer/CTAs próprios.
 */
const BARE_LANDING_ROUTES = new Set<string>(["/magistral", "/forms1", "/forms-full"]);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 pt-24">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold gradient-text">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          O conteúdo que você procura não existe ou foi movido.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="gradient-primary inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-glow hover:opacity-95"
          >
            Voltar para a home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight">Erro ao carregar a página</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo deu errado. Tente novamente ou volte para a home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="gradient-primary inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white"
          >
            Tentar novamente
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium hover:bg-white/10"
          >
            Voltar para home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Kentro Sistemas Inteligentes" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Kentro Sistemas Inteligentes" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#3D1198" },
      { name: "google-site-verification", content: "JbJr_NrtC3jjqQvQMXWVq3WkQcweXMB9b7yztmXf56Q" },
      // OG/Twitter image padrão — leaf routes que setam og:image/twitter:image sobrescrevem.
      // Hospedado em kentro.com.br para evitar a injeção automática de preview (r2.dev).
      { property: "og:image", content: "https://kentro.com.br/og/og-default.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Kentro Sistemas — atendimento omnichannel com Chat, CRM e IA" },
      { name: "twitter:image", content: "https://kentro.com.br/og/og-default.png" },
      { name: "twitter:image:alt", content: "Kentro Sistemas — atendimento omnichannel com Chat, CRM e IA" },
    ],
    links: [
      { rel: "stylesheet", href: appCss, fetchPriority: "high" } as unknown as { rel: string; href: string },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", href: "/favicon-32.png", sizes: "32x32" },
      { rel: "icon", type: "image/png", href: "/icon-192.png", sizes: "192x192" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      // Fontes: self-hosted (subset latin, woff2) via @fontsource em src/styles.css.
      // Nenhuma conexão com fonts.googleapis.com / fonts.gstatic.com.

    ],
    scripts: [
      {
        children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','G-KFBEE985G3');function __kentroLoadGtm(){if(window.__kentroGtmLoaded)return;try{var raw=localStorage.getItem('kentro-cookie-consent');if(!raw)return;var c=JSON.parse(raw);if(!c||!c.analytics)return;}catch(e){return;}window.__kentroGtmLoaded=true;var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=G-KFBEE985G3';document.head.appendChild(s);}if(document.readyState==='complete'){setTimeout(__kentroLoadGtm,1500);}else{window.addEventListener('load',function(){setTimeout(__kentroLoadGtm,1500);},{once:true});}`,
      },

      {
        // JSON-LD consolidado: um único nó Organization/LocalBusiness (mesma
        // entidade, endereço e telefones declarados uma só vez) + WebSite +
        // SoftwareApplication por referência @id. Antes eram 4 nós com dados
        // duplicados em todas as páginas, inflando o HTML inicial.
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["Organization", "LocalBusiness"],
              "@id": "https://www.kentro.com.br/#organization",
              name: "Kentro Sistemas",
              legalName: "Barbalho Solutions Ltda.",
              url: "https://www.kentro.com.br",
              logo: "https://www.kentro.com.br/og-image.png",
              image: "https://www.kentro.com.br/og-image.png",
              email: "contato@kentro.com.br",
              telephone: ["+55-0800-550-2020", "+55-11-91582-7716", "+55-38-3220-8706"],
              priceRange: "$$",
              founder: { "@type": "Person", name: "Vitor Barbalho" },
              address: {
                "@type": "PostalAddress",
                streetAddress: "Alameda Rio Negro, 503, Sala 2020",
                addressLocality: "Barueri",
                addressRegion: "SP",
                postalCode: "06454-000",
                addressCountry: "BR",
              },
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  telephone: "+55-0800-550-2020",
                  email: "contato@kentro.com.br",
                  contactType: "customer support",
                  areaServed: "BR",
                  availableLanguage: ["Portuguese", "English", "Spanish"],
                },
                {
                  "@type": "ContactPoint",
                  telephone: "+55-11-91582-7716",
                  contactType: "sales",
                  areaServed: "BR",
                  availableLanguage: ["Portuguese"],
                },
              ],
              sameAs: [
                "https://www.linkedin.com/company/kentro-sistemas",
                "https://www.instagram.com/kentrosistemas",
                "https://www.facebook.com/kentrosistemas",
                "https://www.youtube.com/@kentrosistemas",
                "https://www.tiktok.com/@kentrooficial",
              ],
              description:
                "Plataforma omnichannel com IA generativa para atendimento, vendas e marketing.",
            },
            {
              "@type": "WebSite",
              "@id": "https://www.kentro.com.br/#website",
              url: "https://www.kentro.com.br",
              name: "Kentro Sistemas",
              inLanguage: "pt-BR",
              publisher: { "@id": "https://www.kentro.com.br/#organization" },
              potentialAction: {
                "@type": "SearchAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: "https://www.kentro.com.br/blog?q={search_term_string}",
                },
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "SoftwareApplication",
              name: "Kentro",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              url: "https://www.kentro.com.br",
              image: "https://www.kentro.com.br/og-image.png",
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "BRL",
                availability: "https://schema.org/InStock",
                url: "https://www.kentro.com.br/contato",
                description: "Planos sob consulta — fale com vendas para uma proposta personalizada.",
              },
              publisher: { "@id": "https://www.kentro.com.br/#organization" },
              description:
                "Central omnichannel com CRM, IA, PABX, BI e automações para WhatsApp, Instagram, e-mail, webchat, SMS e voz.",
            },
          ],
        }),
      },

    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

const OG_LOCALE: Record<string, string> = { pt: "pt_BR", en: "en_US", es: "es_ES" };

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locale = getLocaleFromPathname(pathname);
  const lang = LOCALE_HTML_LANG[locale];
  const ogLocale = OG_LOCALE[locale];
  const alternates = (Object.keys(OG_LOCALE) as Array<keyof typeof OG_LOCALE>)
    .filter((l) => l !== locale)
    .map((l) => OG_LOCALE[l]);
  return (
    <html lang={lang}>
      <head>
        <meta property="og:locale" content={ogLocale} />
        {alternates.map((alt) => (
          <meta key={alt} property="og:locale:alternate" content={alt} />
        ))}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){
              try{
                var t=localStorage.getItem('kentro-theme');
                if(t==='light'){document.documentElement.classList.add('light');}
              }catch(e){}
              try{
                // pt-BR é o idioma padrão. Não fazemos nenhum redirect automático
                // baseado em cookie/navigator.language. A troca de idioma só
                // acontece quando o usuário clica no menu (LocaleSwitcher),
                // que navega diretamente para /en ou /es.
                var path=window.location.pathname||'/';
                var first=path.split('/').filter(Boolean)[0];
                var lang=(first==='en')?'en':(first==='es')?'es':'pt-BR';
                document.documentElement.setAttribute('lang',lang);
              }catch(e){}
            })();`,
          }}
        />



        <HeadContent />

      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  // Consent Mode v2 — define defaults negados o mais cedo possível,
  // ANTES do gtag.js ser carregado pelo banner. Sinais coerentes p/
  // GA4, GTM, Google Ads e Meta Pixel.
  useEffect(() => {
    import("../lib/cookie-consent").then((m) => m.initConsentDefaults());
  }, []);

  useEffect(() => {
    const run = () => import("../lib/tracking").then((m) => m.initTracking());
    const w = window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 1500);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <LocaleSyncedShell />
      </LocaleProvider>
    </QueryClientProvider>
  );
}

function LocaleSyncedShell() {
  const locale = useLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const normalized = stripLocale(pathname).replace(/\/+$/, "") || "/";
  // LPs de tráfego pago isoladas: sem header/footer/CTAs globais (convertem melhor sem menu).
  const isBareLanding = BARE_LANDING_ROUTES.has(normalized);
  const showGlobalForm = !isBareLanding && !ROUTES_WITH_OWN_FORM.has(normalized);

  // Registra a origem real (LP de entrada + página anterior) para atribuição de leads
  useEffect(() => {
    import("../lib/tracking").then((m) => m.recordPageView(pathname));
  }, [pathname]);

  // Mantém <html lang> sincronizado com o locale ativo
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", LOCALE_HTML_LANG[locale]);
    }
  }, [locale]);

  // Não persistimos cookie de locale automaticamente a cada navegação:
  // só o LocaleSwitcher grava o cookie, garantindo que apenas a troca
  // explícita do usuário no menu altere a preferência de idioma.


  // Difere montagem dos widgets flutuantes até o navegador ficar ocioso, para não
  // competir com a renderização inicial (LCP) nem com hydration.
  const [deferReady, setDeferReady] = useState(false);
  useEffect(() => {
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    };
    const cb = () => setDeferReady(true);
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(cb, { timeout: 2500 });
    } else {
      setTimeout(cb, 1800);
    }
  }, []);

  return (
    <>
      <OpenAiPixel />
      {/* Meta Pixel global: PageView em toda navegação (SPA) e eventos de lead
          disparados por formulários de contato, botão flutuante e /forms1 e /forms-full. */}
      <MetaPixel key={pathname} pixelId={META_PIXEL_ID} />
      {!isBareLanding && <SiteHeader />}
      <main className={isBareLanding ? "min-h-screen" : "min-h-screen pt-16 pb-24 md:pb-0"}>
        <Outlet />
        {showGlobalForm && (
          <Suspense fallback={null}>
            <FinalCtaForm />
          </Suspense>
        )}
      </main>
      {!isBareLanding && <SiteFooter />}
      {deferReady && (
        <Suspense fallback={null}>
          {!isBareLanding && (
            <>
              <FloatingWhatsApp />
              <AtenderbemWebchat />
              <MobileStickyCTA />
            </>
          )}
          <CookieConsentBanner />
        </Suspense>
      )}
      <Toaster position="top-right" />
    </>
  );
}
